import { useCallback, useState } from 'react';
import type { JSX } from 'react';

import qs from 'qs';

import { Link, Text } from '@change/ds-core-ui';
import { CaretDownIcon, CaretUpIcon } from '@change/ds-icons';

import { memo } from '@change-corgi/core-react/core';
import { Translate, TranslatePluralHtml } from '@change-corgi/core-react/i18n';

import { usePetitionPageMovementLinkTracking } from '#src/app/pages/petitionDetails/shared/hooks';

type MovementCardProps = {
	movement: { slug: string; objective: string; petitionCount: number };
	petitionId: string;
	alwaysExpanded?: boolean;
};

export const MovementCard = memo<MovementCardProps>(function MovementCard({
	movement: { slug, objective, petitionCount },
	petitionId,
	alwaysExpanded,
}): JSX.Element {
	const [displayedInnerBox, setDisplayedInnerBox] = useState(alwaysExpanded);
	const toggleInnerBox = useCallback(() => setDisplayedInnerBox((val) => !val), [setDisplayedInnerBox]);
	const { trackClick } = usePetitionPageMovementLinkTracking();

	const onMovementNameLinkClick = useCallback((): void => {
		trackClick('movement_name');
	}, [trackClick]);

	const onMovementLearnMoreLinkClick = useCallback((): void => {
		trackClick('learn_more');
	}, [trackClick]);

	const movementUrl = `/m/${slug}${qs.stringify(
		{
			source_location: 'petition_page',
			source_event: 'movement_indicator',
			petition_id: petitionId,
		},
		{ addQueryPrefix: true },
	)}`;

	return (
		<div className="rounded-medium bg-background-raised">
			{/* removing the border width to get the desired height when collapsed */}
			<div className="mx-4 flex h-[42px] items-center">
				<Text as="div" fontWeight="bold" truncate className="mr-4 flex-1">
					<Translate value="corgi.movement.petition.movement" />
					<Link
						href={movementUrl}
						onClick={onMovementNameLinkClick}
						data-testid="movement-name-link"
						data-qa="movement-name-link"
						className="mx-1"
					>
						{objective}
					</Link>
				</Text>
				{!alwaysExpanded && (
					<button
						type="button"
						className="m-0 flex cursor-pointer items-center border-0 bg-transparent p-0"
						onClick={toggleInnerBox}
						aria-expanded={displayedInnerBox}
						data-testid="toggle-button"
						data-qa="toggle-button"
					>
						{displayedInnerBox ? <CaretUpIcon size={28} /> : <CaretDownIcon size={28} />}
						{/* using this approach for a11y for wider screen reader support
							https://www.sarasoueidan.com/blog/accessible-icon-buttons/ (technique #1) */}
						<span className="sr-only">
							<Translate value="corgi.petition.details.movement.toggle_details" />
						</span>
					</button>
				)}
			</div>
			{displayedInnerBox && (
				<Text as="div" className="mx-4 pb-2">
					{petitionCount === 1 ? (
						<Translate value="corgi.movement.petition.belongs.first" replacements={{ name: objective }} />
					) : (
						<TranslatePluralHtml
							value="corgi.movement.petition.belongs.plural"
							count={petitionCount}
							replacements={{ name: objective, petitionCount }}
						/>
					)}
					<Link
						href={movementUrl}
						onClick={onMovementLearnMoreLinkClick}
						data-testid="learn-more-link"
						data-qa="learn-more-link"
						className="pl-1"
					>
						<Translate value="corgi.movement.petition.learn_more" />
					</Link>
				</Text>
			)}
		</div>
	);
});
